import { CssProperties } from "@/states/page-editor/template-parts/common";
import Link from "next/link";
import React, { ReactNode } from "react";

export type Size =
  | "sm"
  | "sm-fix"
  | "md"
  | "md-fix"
  | "lg"
  | "lg-fix"
  | "xl"
  | "input";
export type Color =
  | "default"
  | "primary"
  | "primary-outline"
  | "danger"
  | "danger-transparent"
  | "secondary"
  | "secondary-yellow"
  | "secondary-outline"
  | "delete-transparent"
  | "delete"
  | "cancel"
  | "transparent"
  | "comingsoon"
  | "bg-white";

/**
 * サイズをTailwindクラスに変換
 * @param {Size} s
 */
const sizeToClass = (s?: Size): string => {
  switch (s) {
    case "sm-fix":
      return "md:flex md:min-h-[32px] md:w-full md:py-1.5 md:px-4 md:rounded";
    case "sm":
      return "md:px-4 md:text-sm md:w-auto md:py-1.5 md:h-8 md:rounded";
    case "lg-fix":
      return "md:h-[49px] md:w-full md:px-3 md:rounded-lg";
    case "lg":
      return "md:md:w-auto md:h-[49px] md:px-6 md:rounded-lg";
    case "xl":
      return "font-bold md:h-[56px] md:px-6 md:rounded text-xl border-[#3ABCA8] w-full md:w-[320px]";
    case "md-fix":
      return "md:w-full md:h-10 md:rounded";
    case "md":
    default:
      return "md:w-auto md:py-2 md:px-4 md:rounded";
  }
};

const sizeToSPClass = (s?: Size): string => {
  switch (s) {
    case "sm-fix":
      return "flex min-h-[40px] w-full py-1.5 px-4 rounded";
    case "sm":
      return "inline-flex min-h-[40px] py-1.5 px-4 rounded";
    case "lg-fix":
      return "flex h-[49px] w-full px-6 rounded-lg";
    case "lg":
      return "inline-flex h-[49px] px-6 rounded-lg";
    case "md-fix":
      return "flex w-full h-10 rounded";
    case "xl":
      return "md:md:w-auto font-bold h-[56px] px-4 md:px-6 rounded text-xl";
    case "md":
    default:
      return "inline-flex h-10 px-4 rounded";
  }
};

/**
 * カラーをTailwindクラスに変換
 * @param {Color} c
 */
export function colorToClasses(c?: Color) {
  switch (c) {
    case "secondary":
      return "text-white bg-theme-medium hover:bg-[#808489]";
    case "secondary-yellow":
      return "text-white bg-secondary hover:bg-secondary-light";
    case "secondary-outline":
      return "bg-transparent border border-theme-medium text-theme-medium hover:bg-theme-hover";
    case "cancel":
      return "bg-white border border-theme-medium text-theme hover:bg-theme-hover";
    case "delete":
      return "bg-accent text-white hover:bg-accent-dark";
    case "delete-transparent":
      return "bg-transparent text-accent border border-transparent hover:bg-accent-hover hover:text-white hover:border-accent";
    case "transparent":
      return "bg-transparent text-theme hover:bg-theme-hover";
    case "comingsoon":
      return "text-white bg-theme-medium";
    case "primary-outline":
      return "bg-transparent border border-primary text-primary hover:bg-primary-hover";
    case "bg-white":
      return "bg-white border border-primary text-primary hover:bg-primary-hover";
    case "primary":
    default:
      return "text-white bg-primary hover:bg-primary-dark";
  }
}

interface Props {
  label: string | ReactNode;
  className?: string;
  size?: Size;
  spSize?: Size;
  color?: Color;
  onClick?: () => void | Promise<void>;
  disabled?: boolean;
  href?: string;
  target?: "_blank";
  type?: "button" | "submit";
  style?: CssProperties;
}

/**
 * Basicな共通のボタンです。
 * @param props
 * @returns
 */
const ButtonBase: React.FC<Props> = ({
  label,
  className,
  onClick,
  disabled,
  href,
  size,
  spSize,
  color,
  type = "button",
  ...other
}) => {
  // サイズ指定
  const sizeClass = sizeToClass(size);
  const sizeSpClass = sizeToSPClass(spSize ?? size);
  const colorClasses = colorToClasses(color);
  const baseClasses =
    "items-center justify-center leading-tight text-center md:transition disabled:pointer-events-none md:font-bold disabled:opacity-30";
  const classNames = [
    baseClasses,
    sizeSpClass,
    sizeClass,
    colorClasses,
    className,
  ].join(" ");

  return (
    <>
      {href ? (
        <Link href={href} className={classNames} {...other}>

          {label}

        </Link>
      ) : (
        <button
          className={classNames}
          onClick={onClick}
          disabled={disabled}
          type={type}
          {...other}
        >
          {label}
        </button>
      )}
    </>
  );
};

export const Button = React.memo(ButtonBase);
